import { observer } from 'src/utils/mobx-react';

import {
    PopupDescription,
    LinkWrapper,
} from 'src/domains/players/webview/components/Account/Notifications/BlockingNotifications.style';
import React from 'react';
import { UniverseComponent } from 'src/domains/common/universes';
import { useCommon } from 'src/domains/common/Common';
import { ConfigComponents } from 'src/domains/layouts/config/features/config';
import { GamblingSafetyTopBar } from 'src/domains/layouts/webview/components/gamblingSafetyTopBar/GamblingSafetyTopBar';
import { Footer } from 'src/domains/layouts/webview/modules/layoutSkeleton/Footer';
import { StarRouter } from 'src/domains/layouts/state/router/StarRouter';
import {
    BodySection,
    ClosureOverlay,
    ClosureStack,
    Content,
    FooterBlock,
    GamblingSafetyTopSlot,
    LogoRow,
    PopupHeader,
    SectionHeading,
} from './ClosureModeComponent.style';
import { I18n } from 'src/domains/layouts/webview/components/language/I18n';
import { FooterCopyright } from 'src/domains/layouts/webview/modules/layoutSkeleton/footer/copyright/FooterCopyright';

const GENTLEMAN_JIM_SUPPORT_EMAIL = 'support@gentlemanjim.bet';

export const ClosureModeComponent = observer(
    'ClosureModeComponent',
    ({ alreadyClosed = false }: { alreadyClosed?: boolean }): JSX.Element | null => {
        const common = useCommon();
        const router = StarRouter.get(common);
        const { isSaferGambling, operatorFullName } = ConfigComponents.get(common).config;
        const showGamblingSafetyStrip = isSaferGambling.available === true;
        const mailtoSupport = `mailto:${GENTLEMAN_JIM_SUPPORT_EMAIL}`;

        return (
            <ClosureOverlay
                data-test='overlay-prompt'
                alreadyClosed={alreadyClosed}
            >
                <ClosureStack>
                    {showGamblingSafetyStrip ? (
                        <GamblingSafetyTopSlot>
                            <GamblingSafetyTopBar />
                        </GamblingSafetyTopSlot>
                    ) : null}
                    <Content>
                        <LogoRow>
                            <UniverseComponent>
                                {(universeModule): React.ReactElement => <universeModule.compliancePopupLogo />}
                            </UniverseComponent>
                        </LogoRow>

                        <BodySection>
                            <PopupHeader>
                                <I18n
                                    langKey='account.closure.title'
                                    defaultText='{operatorFullName} has closed'
                                    params={{ operatorFullName }}
                                />
                            </PopupHeader>
                            <PopupDescription>
                                <I18n
                                    langKey='account.closure.intro'
                                    defaultText='{operatorFullName} closed on Wednesday 13 May 2026. We want to thank our customers for their support over the years.'
                                    params={{ operatorFullName }}
                                />
                            </PopupDescription>
                            {alreadyClosed ? null : (
                                <>
                                    <SectionHeading>
                                        <I18n
                                            langKey='account.closure.heading.account-balance'
                                            defaultText='Account balances'
                                        />
                                    </SectionHeading>
                                    <PopupDescription>
                                        <I18n
                                            langKey='account.closure.account-balance'
                                            defaultText='Where a balance remained on an account at closure, we will attempt to transfer it to the registered payment method. If you believe funds are still owed to you, please contact us using the details below, and we will arrange the return of your funds.'
                                        />
                                    </PopupDescription>

                                    <SectionHeading>
                                        <I18n
                                            langKey='account.closure.heading.open-bets'
                                            defaultText='Open and ante-post bets'
                                        />
                                    </SectionHeading>
                                    <PopupDescription>
                                        <I18n
                                            langKey='account.closure.open-bets'
                                            defaultText="All bets placed before 13 May 2026 will be honoured. Bets that settled before closure were credited to accounts as usual. Winnings from bets settling on or after 13th May 2026 will be paid by our team to customers' registered payment methods where possible."
                                        />
                                    </PopupDescription>
                                </>
                            )}
                            <PopupDescription>
                                <I18n
                                    langKey='account.closure.support.before-email'
                                    defaultText='For any queries relating to your former {operatorFullName} account, your balance, or open bets, contact '
                                    params={{ operatorFullName }}
                                />
                                <LinkWrapper to={mailtoSupport}>
                                    <I18n
                                        langKey='account.closure.support-email'
                                        defaultText='support@gentlemanjim.bet'
                                    />
                                </LinkWrapper>
                            </PopupDescription>
                            <PopupDescription>
                                <I18n
                                    langKey='account.closure.thank-you'
                                    defaultText='Thank you for being a {operatorFullName} customer.'
                                    params={{ operatorFullName }}
                                />
                            </PopupDescription>
                            <PopupDescription>
                                <I18n
                                    langKey='account.closure.signature'
                                    defaultText='The {operatorFullName} Team.'
                                    params={{ operatorFullName }}
                                />
                            </PopupDescription>
                        </BodySection>
                    </Content>

                    <FooterBlock alreadyClosed={alreadyClosed}>
                        {alreadyClosed ? (
                            <FooterCopyright alreadyClosed={alreadyClosed} />
                        ) : (
                            <Footer currentView={router.currentView} />
                        )}
                    </FooterBlock>
                </ClosureStack>
            </ClosureOverlay>
        );
    }
);
